import React, {useEffect, useRef, useState} from 'react';

import CSSModules from 'react-css-modules';
import type {YouTubeProps} from 'react-youtube';
import YouTube from 'react-youtube';

import styles from './styles.module.scss';

type YouTubeContentProps = {
  youtubeId?: string;
  onPlay?: () => void;
  onFinish?: () => void;
  autoPlay?: boolean;
  delayStart?: boolean;
  isSkippable?: boolean;
};

// This is a bit hacky but seems like there's a known TS issue (albeit from a while ago)
// https://github.com/tjallingt/react-youtube/issues/211
type YouTubePlayerRef = YouTube & {
  internalPlayer: {
    playVideo: () => void;
  };
};

const YouTubeContent = ({
  youtubeId,
  onPlay,
  onFinish,
  autoPlay = false,
  delayStart = false,
  isSkippable = true,
}: YouTubeContentProps): JSX.Element => {
  const startTimeout = useRef<number | null>(null);
  const player = useRef<YouTubePlayerRef>(null);
  const [didAutoplay] = useState(autoPlay);

  useEffect(() => {
    if (autoPlay && !didAutoplay && player.current) {
      player.current?.internalPlayer?.playVideo();
    }
  }, [autoPlay]);

  useEffect(
    () => () => {
      if (startTimeout.current) {
        window.clearTimeout(startTimeout.current);
      }
    },
    []
  );

  const onReady: YouTubeProps['onReady'] = (event) => {
    if (autoPlay) {
      const timeout = delayStart ? 400 : 0;
      startTimeout.current = window.setTimeout(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        () => event.target.playVideo(),
        timeout
      );
    }
  };

  const onStateChange: YouTubeProps['onStateChange'] = (event) => {
    if (event.data === YouTube.PlayerState.PLAYING && onPlay) {
      onPlay();
    }
    if (event.data === YouTube.PlayerState.ENDED && onFinish) {
      onFinish();
    }
  };

  return (
    <div styleName='videoContainer'>
      <YouTube
        ref={player}
        className='video'
        videoId={youtubeId}
        opts={{
          playerVars: {
            modestbranding: 1,
            rel: 0,
            controls: isSkippable ? 1 : 0,
            disablekb: isSkippable ? 0 : 1,
          },
        }}
        onReady={onReady}
        onStateChange={onStateChange}
      />
    </div>
  );
};

export default CSSModules(YouTubeContent, styles);
